import { useState } from "react";
import { toast } from "sonner";
import { DeviceMobile, Warning, CircleNotch } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MomoPaymentForm, NETWORKS } from "@/components/MomoPaymentForm";

export default function MobileMoneyDialog({ open, onOpenChange, invoice, onPaid }) {
  const [stage, setStage] = useState("form"); // form | pending | not_configured
  const [pendingPhone, setPendingPhone] = useState("");
  const [pendingNetwork, setPendingNetwork] = useState("MTN");

  const reset = () => setStage("form");

  const poll = async (txRef) => {
    for (let i = 0; i < 20; i++) {
      await new Promise((r) => setTimeout(r, 4000));
      try {
        const { data } = await api.get(`/payments/momo/${txRef}`);
        if (data.status === "successful") {
          toast.success("Mobile money payment received");
          onPaid();
          onOpenChange(false);
          reset();
          return;
        }
        if (["failed", "cancelled"].includes(data.status)) {
          toast.error("Payment was not completed on the phone");
          setStage("form");
          return;
        }
      } catch (err) {
        console.warn("Mobile money status poll failed — will retry", err);
      }
    }
    toast.info("Payment still pending — check the invoice shortly");
    onOpenChange(false);
    reset();
  };

  const handlePending = (txRef, phone, network) => {
    setPendingPhone(phone);
    setPendingNetwork(network);
    setStage("pending");
    poll(txRef);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent data-testid="mobile-money-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2 text-xl">
            <DeviceMobile size={22} weight="duotone" className="text-primary" /> Pay with Mobile Money
          </DialogTitle>
        </DialogHeader>

        {stage === "not_configured" && (
          <div className="space-y-4" data-testid="momo-not-configured">
            <div className="flex items-start gap-3 border border-status-pending/40 bg-status-pending/10 p-4 text-sm">
              <Warning size={20} className="mt-0.5 shrink-0 text-status-pending" />
              <div>
                <div className="font-semibold">Mobile money is not configured yet</div>
                <p className="mt-1 text-muted-foreground">
                  The full MTN / Airtel / Zamtel flow is built and ready. To go live, add your Flutterwave
                  test/live keys (<code>FLW_SECRET_KEY</code> and <code>FLW_SECRET_HASH</code>) to the backend
                  environment — guests will then get a PIN prompt on their phone and the invoice settles itself.
                </p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Until then, use <strong>Record payment</strong> to log mobile money received manually.
            </p>
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="momo-close" className="w-full transition-colors">
              Close
            </Button>
          </div>
        )}

        {stage === "pending" && (
          <div className="space-y-4 py-4 text-center" data-testid="momo-pending">
            <CircleNotch size={40} className="mx-auto animate-spin text-primary" />
            <div className="font-heading text-lg font-bold">Prompt sent to •••{pendingPhone.slice(-4)}</div>
            <p className="text-sm text-muted-foreground">
              Ask the guest to approve the {NETWORKS.find((n) => n.value === pendingNetwork)?.label} prompt on their
              phone and enter their mobile money PIN. This window will update automatically.
            </p>
          </div>
        )}

        {stage === "form" && (
          <MomoPaymentForm invoice={invoice} onPending={handlePending} onNotConfigured={() => setStage("not_configured")} />
        )}
      </DialogContent>
    </Dialog>
  );
}
